// Social Media Integration Configuration
export interface SocialMediaConfig {
  facebook: {
    appId: string;
    redirectUri: string;
    permissions: string[];
  };
}

// Get environment-specific redirect URI for Facebook OAuth
export const getFacebookRedirectUri = (): string => {
  // Use environment variables for production URLs
  const prodUrl = process.env.REACT_APP_FACEBOOK_REDIRECT_URI_PROD;
  const amplifyUrl = process.env.REACT_APP_FACEBOOK_REDIRECT_URI_AMPLIFY;
  const localUrl = process.env.REACT_APP_FACEBOOK_REDIRECT_URI_LOCAL;
  
  const origin = window.location.origin;
  
  // Production domain (custom domain)
  if (origin.includes('bigsystemone.com')) {
    return prodUrl || 'https://bigsystemone.com/social-media/facebook-callback';
  }
  
  // Main Amplify production domain (from build logs: d2h2cbbgz5s714)
  if (origin.includes('main.d2h2cbbgz5s714.amplifyapp.com')) {
    return amplifyUrl || 'https://main.d2h2cbbgz5s714.amplifyapp.com/social-media/facebook-callback';
  }
  
  // Any other Amplify domains (preview branches, etc.)
  if (origin.includes('amplifyapp.com')) {
    return `${origin}/social-media/facebook-callback`;
  }
  
  // Local development
  if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
    return localUrl || 'http://localhost:3000/social-media/facebook-callback';
  }
  
  // Default fallback for any other domain
  return `${origin}/social-media/facebook-callback`;
};

// Social Media Configuration
export const SOCIAL_MEDIA_CONFIG: SocialMediaConfig = {
  facebook: {
    appId: process.env.REACT_APP_FACEBOOK_APP_ID || '1749465492375238',
    redirectUri: getFacebookRedirectUri(),
    permissions: [
      'pages_manage_posts',
      'pages_read_engagement',
      'pages_show_list',
      'business_management',
      'read_insights',
      'public_profile'
    ]
  }
};

// Facebook OAuth URL builder
export const buildFacebookOAuthUrl = (storeId: string, storeName: string): string => {
  const config = SOCIAL_MEDIA_CONFIG.facebook;
  const oauthState = JSON.stringify({
    storeId,
    storeName,
    timestamp: Date.now()
  });

  const facebookOAuthUrl = new URL('https://www.facebook.com/v22.0/dialog/oauth');
  facebookOAuthUrl.searchParams.set('client_id', config.appId);
  facebookOAuthUrl.searchParams.set('redirect_uri', config.redirectUri);
  facebookOAuthUrl.searchParams.set('scope', config.permissions.join(','));
  facebookOAuthUrl.searchParams.set('state', btoa(oauthState));
  facebookOAuthUrl.searchParams.set('response_type', 'code');

  return facebookOAuthUrl.toString();
};

// LinkedIn OAuth Configuration
export const LINKEDIN_CLIENT_ID = process.env.REACT_APP_LINKEDIN_CLIENT_ID || '78ihv0dp6i9r3t';

export const getLinkedInRedirectUri = (): string => {
  const origin = window.location.origin;

  if (origin.includes('bigsystemone.com')) {
    return 'https://bigsystemone.com/social-media/linkedin/callback';
  }
  if (origin.includes('main.d2h2cbbgz5s714.amplifyapp.com')) {
    return 'https://main.d2h2cbbgz5s714.amplifyapp.com/social-media/linkedin/callback';
  }
  if (origin.includes('amplifyapp.com')) {
    return `${origin}/social-media/linkedin/callback`;
  }
  if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
    return 'http://localhost:3000/social-media/linkedin/callback';
  }
  return `${origin}/social-media/linkedin/callback`;
};

export const buildLinkedInOAuthUrl = (storeId: string, storeName: string): string => {
  const oauthState = JSON.stringify({
    storeId,
    storeName,
    timestamp: Date.now()
  });

  const scopes = [
    'w_member_social',
    'r_organization_social',
    'w_organization_social'
  ];

  const linkedInOAuthUrl = new URL('https://www.linkedin.com/oauth/v2/authorization');
  linkedInOAuthUrl.searchParams.set('response_type', 'code');
  linkedInOAuthUrl.searchParams.set('client_id', LINKEDIN_CLIENT_ID);
  linkedInOAuthUrl.searchParams.set('redirect_uri', getLinkedInRedirectUri());
  linkedInOAuthUrl.searchParams.set('scope', scopes.join(' '));
  linkedInOAuthUrl.searchParams.set('state', btoa(oauthState));

  return linkedInOAuthUrl.toString();
};

// Debug helper to log current OAuth configuration
export const logOAuthConfig = (): void => {
  const origin = window.location.origin;
  const hostname = window.location.hostname;
  
  console.log('Social Media OAuth Configuration:', {
    environment: process.env.NODE_ENV,
    origin: origin,
    hostname: hostname,
    href: window.location.href,
    facebook: {
      appId: SOCIAL_MEDIA_CONFIG.facebook.appId,
      redirectUri: SOCIAL_MEDIA_CONFIG.facebook.redirectUri,
      permissions: SOCIAL_MEDIA_CONFIG.facebook.permissions
    }
  });
  
  // Additional debugging for domain detection
  console.log('Domain Detection:', {
    isProductionBSO: origin.includes('bigsystemone.com'),
    isMainAmplify: origin.includes('main.d2h2cbbgz5s714.amplifyapp.com'),
    isAnyAmplify: origin.includes('amplifyapp.com'),
    isLocalhost: origin.includes('localhost') || origin.includes('127.0.0.1')
  });
  
  // Alert for configuration issues
  if (origin.includes('amplifyapp.com') && !origin.includes('main.d2h2cbbgz5s714')) {
    console.warn('WARNING: Using unexpected Amplify domain. May need Facebook App configuration update:', origin);
  }
};